import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { causeCoverUrl, causeQrUrl, globalQrUrl } from "@/lib/payments";
import { toast } from "sonner";
import { Plus, Trash2, Save, Upload, X, KeyRound, QrCode, CreditCard, HandHeart, Eye, EyeOff } from "lucide-react";

const inpCls = "w-full px-3 py-2.5 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-emerald-700 bg-white";

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

// =============== Causes ===============
export function CausesManager() {
  const [items, setItems] = useState([]);
  const empty = { title_en: "", title_ta: "", description_en: "", description_ta: "", target_amount: 0, deadline: "", status: "active", order: 0 };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/causes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title_en) return toast.error("Title (English) is required");
    const payload = {
      ...form,
      target_amount: parseFloat(form.target_amount || 0),
      order: parseInt(form.order || 0),
      deadline: form.deadline || null,
    };
    try {
      if (editingId) {
        await api.put(`/causes/${editingId}`, payload);
        toast.success("Cause updated");
      } else {
        await api.post("/causes", payload);
        toast.success("Cause added");
      }
      setEditingId(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title_en: c.title_en, title_ta: c.title_ta || "",
      description_en: c.description_en || "", description_ta: c.description_ta || "",
      target_amount: c.target_amount || 0,
      deadline: c.deadline ? c.deadline.slice(0, 10) : "",
      status: c.status || "active",
      order: c.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (id) => { if (!confirm("Delete this cause?")) return; await api.delete(`/causes/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="fade-up">
      <div className="mb-6">
        <h2 className="font-serif-en text-3xl text-emerald-950">Fundraising Causes</h2>
        <p className="text-stone-500 text-sm mt-1">Add specific causes with target amount, cover image and a QR code</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-5 mb-6 grid md:grid-cols-2 gap-4">
        <Field label="Title (English)"><input data-testid="cs-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inpCls} placeholder="e.g. New Wudu Area" /></Field>
        <Field label="தலைப்பு (Tamil)"><input data-testid="cs-title-ta" value={form.title_ta} onChange={(e) => setForm({ ...form, title_ta: e.target.value })} className={inpCls} /></Field>
        <Field label="Description (English)" full><textarea data-testid="cs-desc-en" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className={`${inpCls} h-24`} /></Field>
        <Field label="விளக்கம் (Tamil)" full><textarea data-testid="cs-desc-ta" value={form.description_ta} onChange={(e) => setForm({ ...form, description_ta: e.target.value })} className={`${inpCls} h-24`} /></Field>
        <Field label="Target Amount (₹)"><input data-testid="cs-target" type="number" min="0" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className={inpCls} /></Field>
        <Field label="Deadline (optional)"><input data-testid="cs-deadline" type="date" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inpCls} /></Field>
        <Field label="Status">
          <select data-testid="cs-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inpCls}>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </Field>
        <Field label="Display Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={inpCls} /></Field>
        <div className="md:col-span-2 flex items-center gap-3">
          <button data-testid="save-cause-btn" onClick={submit} className="btn-primary inline-flex items-center gap-2">
            {editingId ? <><Save className="w-4 h-4" /> Update Cause</> : <><Plus className="w-4 h-4" /> Add Cause</>}
          </button>
          {editingId && <button onClick={() => { setEditingId(null); setForm(empty); }} className="btn-ghost-emerald">Cancel</button>}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center text-stone-500 py-10 bg-white border border-dashed border-stone-300 rounded-lg">No causes yet</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((c, i) => <CauseRow key={c.id} c={c} i={i} onChanged={load} onEdit={() => startEdit(c)} onDelete={() => del(c.id)} />)}
        </div>
      )}
    </div>
  );
}

function CauseRow({ c, i, onChanged, onEdit, onDelete }) {
  const coverRef = useRef(null);
  const qrRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/causes/${c.id}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "cover" ? "Cover" : "QR"} updated`);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (coverRef.current) coverRef.current.value = ""; if (qrRef.current) qrRef.current.value = ""; }
  };

  return (
    <div data-testid={`admin-cause-${i}`} className="bg-white border border-stone-200 rounded-lg overflow-hidden">
      <div className="relative h-32 bg-stone-100">
        {c.cover_path
          ? <img src={`${causeCoverUrl(c.id)}?v=${encodeURIComponent(c.cover_path)}`} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-stone-400 text-xs">No cover image</div>}
        <span className={`absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded ${c.status === "active" ? "bg-emerald-900 text-white" : "bg-stone-300 text-stone-700"}`}>{c.status}</span>
      </div>
      <div className="p-4">
        <div className="font-medium text-emerald-950">{c.title_en}</div>
        <div className="text-xs text-stone-500 mt-1">Target: ₹{Number(c.target_amount || 0).toLocaleString("en-IN")} · Raised: ₹{Number(c.raised_amount || 0).toLocaleString("en-IN")}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <input ref={coverRef} data-testid={`cs-cover-${i}`} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0], "cover")} className="hidden" />
          <button data-testid={`cs-cover-btn-${i}`} disabled={busy} onClick={() => coverRef.current?.click()} className="text-xs px-2.5 py-1 border border-emerald-900 text-emerald-900 rounded-md hover:bg-emerald-900 hover:text-white transition-colors disabled:opacity-60">
            {c.cover_path ? "Replace Cover" : "Upload Cover"}
          </button>
          <input ref={qrRef} data-testid={`cs-qr-${i}`} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0], "qr")} className="hidden" />
          <button data-testid={`cs-qr-btn-${i}`} disabled={busy} onClick={() => qrRef.current?.click()} className="text-xs px-2.5 py-1 border border-amber-700 text-amber-700 rounded-md hover:bg-amber-700 hover:text-white transition-colors disabled:opacity-60">
            {c.qr_path ? "Replace QR" : "Upload QR"}
          </button>
          {c.qr_path && (
            <a href={`${causeQrUrl(c.id)}?v=${encodeURIComponent(c.qr_path)}`} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 border border-stone-300 rounded-md hover:bg-stone-50 inline-flex items-center gap-1">
              <QrCode className="w-3 h-3" /> View QR
            </a>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <button data-testid={`edit-cause-${i}`} onClick={onEdit} className="px-2.5 py-1 border border-stone-300 rounded-md hover:bg-stone-50">Edit</button>
          <button data-testid={`del-cause-${i}`} onClick={onDelete} className="text-red-600 hover:bg-red-50 p-1.5 rounded-md inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        </div>
      </div>
    </div>
  );
}

// =============== Payment Settings ===============
export function PaymentSettingsManager() {
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "", upi_id: "" });
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [pub, setPub] = useState(null);
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/payment-settings/admin").then((r) => r.data),
      api.get("/payment-settings").then((r) => r.data),
    ]);
    setForm(a); setPub(p);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/payment-settings", form); toast.success("Payment settings saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post("/payment-settings/global-qr", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Global QR uploaded"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (qrRef.current) qrRef.current.value = ""; }
  };
  const removeQr = async () => { setBusy(true); try { await api.delete("/payment-settings/global-qr"); toast.success("QR removed"); load(); } finally { setBusy(false); } };

  return (
    <div className="fade-up">
      <div className="mb-6">
        <h2 className="font-serif-en text-3xl text-emerald-950">Payment Settings</h2>
        <p className="text-stone-500 text-sm mt-1">Configure Razorpay keys, UPI, and global QR code</p>
      </div>

      {/* Status banner */}
      <div className={`mb-6 p-4 rounded-lg border ${pub?.razorpay_enabled ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
        <div className="flex items-center gap-2 text-sm">
          <CreditCard className="w-4 h-4" />
          <span className="font-medium">Razorpay status:</span> {pub?.razorpay_enabled ? "Configured & live" : "Not configured (online payments disabled)"}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-5 mb-6 space-y-4">
        <div className="flex items-center gap-2 text-emerald-900 font-medium"><KeyRound className="w-4 h-4" /> Razorpay API Credentials</div>
        <Field label="Razorpay Key ID">
          <input data-testid="ps-key-id" value={form.razorpay_key_id || ""} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })} className={inpCls} placeholder="rzp_test_xxxxxxxxxx" />
        </Field>
        <Field label="Razorpay Key Secret">
          <div className="relative">
            <input data-testid="ps-key-secret" type={showSecret ? "text" : "password"} value={form.razorpay_key_secret || ""} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })} className={`${inpCls} pr-10`} placeholder="••••••••••••••••" />
            <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="Webhook Secret (optional)">
          <div className="relative">
            <input data-testid="ps-webhook" type={showWebhook ? "text" : "password"} value={form.razorpay_webhook_secret || ""} onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })} className={`${inpCls} pr-10`} />
            <button type="button" onClick={() => setShowWebhook(!showWebhook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700">
              {showWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <p className="text-xs text-stone-500">Get your keys from <a className="text-emerald-900 underline" href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer">dashboard.razorpay.com → Settings → API Keys</a>. Use test keys (rzp_test_*) until you're ready to go live.</p>
        <hr className="border-stone-200" />
        <Field label="UPI ID (for QR + display)"><input data-testid="ps-upi" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className={inpCls} placeholder="masjidalnoor@upi" /></Field>
        <button data-testid="save-payment-settings" disabled={busy} onClick={save} className="btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Payment Settings</button>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-5">
        <div className="flex items-center gap-2 text-emerald-900 font-medium mb-3"><QrCode className="w-4 h-4" /> Global UPI QR Code</div>
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-48 h-48 bg-stone-50 border border-stone-200 rounded-lg overflow-hidden flex items-center justify-center">
            {pub?.has_global_qr
              ? <img src={`${globalQrUrl()}?v=${Date.now()}`} alt="QR" className="w-full h-full object-contain" />
              : <div className="text-xs text-stone-400 text-center p-4">No QR uploaded</div>}
          </div>
          <div className="flex-1 min-w-[200px]">
            <input ref={qrRef} data-testid="global-qr-input" type="file" accept="image/*" onChange={(e) => uploadQr(e.target.files?.[0])} className="hidden" />
            <button data-testid="upload-global-qr" disabled={busy} onClick={() => qrRef.current?.click()} className="btn-primary inline-flex items-center gap-2"><Upload className="w-4 h-4" /> {pub?.has_global_qr ? "Replace QR" : "Upload QR"}</button>
            {pub?.has_global_qr && <button data-testid="remove-global-qr" onClick={removeQr} className="ml-2 text-sm text-red-600 hover:bg-red-50 px-3 py-2 rounded-md inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Remove</button>}
            <p className="text-xs text-stone-500 mt-3">Generate a UPI QR from your banking app (e.g. GPay → Payments → Show QR), then upload here. Used as fallback when a cause doesn't have its own QR.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============== Donations ===============
export function DonationsManager() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_paid: 0, count_paid: 0 });
  const [filter, setFilter] = useState("paid");
  const load = async () => {
    const url = filter === "all" ? "/donations" : `/donations?status=${filter}`;
    const [d, s] = await Promise.all([
      api.get(url).then((r) => r.data).catch(() => []),
      api.get("/donations/summary").then((r) => r.data).catch(() => ({ total_paid: 0, count_paid: 0 })),
    ]);
    setItems(d); setSummary(s);
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="fade-up">
      <div className="mb-6">
        <h2 className="font-serif-en text-3xl text-emerald-950">Donations</h2>
        <p className="text-stone-500 text-sm mt-1">All transactions processed through Razorpay</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Total Raised" value={`₹${Number(summary.total_paid).toLocaleString("en-IN")}`} />
        <Stat label="Successful Donations" value={summary.count_paid} />
        <Stat label="Showing" value={items.length} />
      </div>

      <div className="flex gap-2 mb-4">
        {["paid", "created", "failed", "all"].map((s) => (
          <button key={s} data-testid={`donations-filter-${s}`} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-emerald-900 text-white border-emerald-900" : "border-stone-300"}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center text-stone-500 py-10 bg-white border border-dashed border-stone-300 rounded-lg">No donations yet</div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Donor</th>
                <th className="text-left p-3">Cause</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d, i) => (
                <tr key={d.id} data-testid={`donation-row-${i}`} className="border-t border-stone-100">
                  <td className="p-3 text-stone-500">{new Date(d.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="p-3">
                    <div className="text-stone-800">{d.donor_name || "—"}</div>
                    <div className="text-xs text-stone-500">{d.donor_phone || d.donor_email || ""}</div>
                  </td>
                  <td className="p-3 text-stone-700">{d.cause_title || "General"}</td>
                  <td className="p-3 text-right font-medium text-emerald-900">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${d.status === "paid" ? "bg-emerald-100 text-emerald-900" : d.status === "failed" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-700"}`}>{d.status}</span>
                  </td>
                  <td className="p-3 text-xs font-mono text-stone-500">{d.razorpay_payment_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-2xl font-light text-emerald-950 mt-1">{value}</div>
    </div>
  );
}
